import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Download, Pencil, CheckCircle2, Archive, Lightbulb, AlertTriangle, HelpCircle, Video, Paperclip, ExternalLink } from "lucide-react";
import CalloutImage from "./CalloutImage";

const DIFF_COLORS = { base: "#059669", intermedio: "#d97706", avanzato: "#dc2626" };
const STATUS_LABELS = { bozza: "Bozza", in_revisione: "In revisione", pubblicata: "Pubblicata", archiviata: "Archiviata" };

function embedUrl(url) {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = url.match(/vimeo\.com\/(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  return null;
}

export default function GuideViewer({ id, meta, onBack, onEdit }) {
  const [g, setG] = useState(null);
  const canManage = meta?.can_manage;

  const load = () => api.get(`/academy/guides/${id}`).then((r) => setG(r.data)).catch(() => toast.error("Errore caricamento guida"));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setStatus = async (status) => {
    try { await api.post(`/academy/guides/${id}/status`, { status }); toast.success("Stato aggiornato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/academy/guides/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `guida.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore generazione PDF"); }
  };

  if (!g) return <p className="py-16 text-center text-sm text-slate-400">Caricamento…</p>;
  const catLabel = (meta?.categories || []).find((c) => c.key === g.category)?.label || g.category;

  return (
    <div className="space-y-6 pb-16" data-testid="guide-viewer">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="guide-back"><ArrowLeft size={18} /></Button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-head text-2xl font-semibold text-slate-900">{g.title}</h1>
              <Badge variant="outline" style={{ borderColor: DIFF_COLORS[g.difficulty], color: DIFF_COLORS[g.difficulty] }}>{g.difficulty}</Badge>
              {g.status !== "pubblicata" && <Badge variant="secondary">{STATUS_LABELS[g.status]}</Badge>}
            </div>
            <p className="mt-1 text-sm text-slate-500">{catLabel}{g.summary ? ` · ${g.summary}` : ""}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={downloadPdf} data-testid="guide-pdf-btn"><Download size={14} /> PDF</Button>
          {canManage && <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => onEdit(g)} data-testid="guide-edit-btn"><Pencil size={14} /> Modifica</Button>}
          {canManage && g.status !== "pubblicata" && <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" onClick={() => setStatus("pubblicata")} data-testid="guide-publish-btn"><CheckCircle2 size={14} /> Pubblica</Button>}
          {canManage && g.status === "pubblicata" && <Button variant="ghost" size="sm" className="gap-1.5 text-amber-600" onClick={() => setStatus("archiviata")}><Archive size={14} /> Archivia</Button>}
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-6">
        {(g.steps || []).map((s) => (
          <Card key={s.id} className="border-slate-200 p-5" data-testid={`guide-step-${s.number}`}>
            <div className="flex items-start gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-bold text-white">{s.number}</span>
              <div className="min-w-0 flex-1">
                {s.title && <h3 className="font-head text-lg font-semibold text-slate-800">{s.title}</h3>}
                {s.text && <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{s.text}</p>}
                {s.image_url && (
                  <div className="mt-3 max-w-2xl">
                    <CalloutImage src={s.image_url} callouts={s.callouts} />
                    {(s.callouts || []).some((c) => c.text) && (
                      <ol className="mt-2 space-y-1">
                        {s.callouts.filter((c) => c.text).map((c) => (
                          <li key={c.id} className="flex gap-2 text-sm text-slate-600">
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white" style={{ backgroundColor: c.color || "#4f46e5" }}>{c.n}</span>
                            {c.text}
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Videos */}
      {(g.videos || []).length > 0 && (
        <Card className="border-slate-200 p-5">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><Video size={16} /> Video</div>
          <div className="grid gap-4 sm:grid-cols-2">
            {g.videos.map((v) => {
              const emb = v.type === "external" ? embedUrl(v.url) : null;
              return (
                <div key={v.id}>
                  {v.type === "upload" ? (
                    <video src={v.url} controls className="w-full rounded-lg border border-slate-200" />
                  ) : emb ? (
                    <div className="aspect-video"><iframe src={emb} title={v.title} className="h-full w-full rounded-lg border border-slate-200" allowFullScreen /></div>
                  ) : (
                    <a href={v.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-indigo-600"><ExternalLink size={14} /> {v.title || v.url}</a>
                  )}
                  {v.title && <p className="mt-1 text-xs text-slate-500">{v.title}</p>}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {(g.tips || []).length > 0 && (
          <Card className="border-emerald-200 bg-emerald-50/50 p-5">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-emerald-700"><Lightbulb size={16} /> Suggerimenti</div>
            <ul className="space-y-1.5 text-sm text-slate-700">{g.tips.map((t, i) => <li key={i} className="flex gap-2"><span className="text-emerald-500">•</span>{t}</li>)}</ul>
          </Card>
        )}
        {(g.common_errors || []).length > 0 && (
          <Card className="border-amber-200 bg-amber-50/50 p-5">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-700"><AlertTriangle size={16} /> Errori comuni</div>
            <div className="space-y-2 text-sm">{g.common_errors.map((e, i) => <div key={i}><p className="font-medium text-slate-700">⚠ {e.problem}</p><p className="text-slate-600">→ {e.solution}</p></div>)}</div>
          </Card>
        )}
      </div>

      {(g.faqs || []).length > 0 && (
        <Card className="border-slate-200 p-5">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><HelpCircle size={16} /> FAQ</div>
          <div className="space-y-3">{g.faqs.map((f, i) => <div key={i}><p className="font-medium text-slate-800">{f.q}</p><p className="mt-0.5 text-sm text-slate-600">{f.a}</p></div>)}</div>
        </Card>
      )}

      {(g.attachments || []).length > 0 && (
        <Card className="border-slate-200 p-5">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><Paperclip size={16} /> Allegati</div>
          <div className="flex flex-wrap gap-2">{g.attachments.map((a) => <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-indigo-600 hover:bg-slate-50"><Paperclip size={13} /> {a.name}</a>)}</div>
        </Card>
      )}
    </div>
  );
}
